import { useContext, useCallback, useEffect } from 'preact/hooks';
import { useSignal } from '@preact/signals';
import { ChatContext } from '../hooks/context.js';
import { renderMarkdown } from '../utils/sanitizer.js';
import { formatTime } from '../utils/helpers.js';
import { ICONS } from '../utils/constants.js';
import { SourcesPanel } from './SourcesPanel.jsx';
import { ToolCallIndicator } from './ToolCallIndicator.jsx';
import { StructuredResponse } from './StructuredResponse.jsx';

function ErrorCard({ msg }) {
  const { bus } = useContext(ChatContext);
  const countdown = useSignal(msg.retryAfter || 0);

  useEffect(() => {
    if (!msg.retryAfter) return;
    countdown.value = msg.retryAfter;
    const iv = setInterval(() => {
      countdown.value -= 1;
      if (countdown.value <= 0) clearInterval(iv);
    }, 1000);
    return () => clearInterval(iv);
  }, [msg.retryAfter]);

  const handleRetry = useCallback(() => {
    if (msg.retryText) bus.emit('_send', { text: msg.retryText });
  }, [msg.retryText]);

  const isRateLimit = msg.errorCode === 'rate_limit' || msg.errorCode === 'provider_rate_limit';
  const isAuth      = msg.errorCode === 'provider_auth_error' || msg.errorCode === 'auth_error';
  const isTimeout   = msg.errorCode === 'provider_timeout';
  const canRetry    = msg.recoverable && msg.retryText && (!msg.retryAfter || countdown.value <= 0);

  let icon = '⚠';
  if (isRateLimit) icon = '⏳';
  else if (isAuth) icon = '🔑';
  else if (isTimeout) icon = '⌛';

  return (
    <div class="mc-system mc-system-error mc-error-card" role="alert">
      <span class="mc-error-icon" aria-hidden="true">{icon}</span>
      <span class="mc-error-msg">
        {isRateLimit && countdown.value > 0
          ? `Too many requests — retry in ${countdown}s`
          : isAuth
          ? 'API configuration error — contact support'
          : msg.text}
      </span>
      {canRetry && (
        <button class="mc-error-retry" onClick={handleRetry} aria-label="Retry">
          Retry
        </button>
      )}
    </div>
  );
}

function CopyButton({ preEl }) {
  const handleCopy = useCallback(() => {
    const code = preEl.querySelector('code')?.textContent || preEl.textContent;
    navigator.clipboard?.writeText(code).then(() => {
      preEl._copyBtn.innerHTML = ICONS.check;
      setTimeout(() => { if (preEl._copyBtn) preEl._copyBtn.innerHTML = ICONS.copy; }, 2000);
    });
  }, [preEl]);
  return null; // injected imperatively via useEffect in BubbleContent
}

function BubbleContent({ text }) {
  const html = renderMarkdown(text);

  // We need ref to attach copy buttons to <pre> blocks after render
  const ref = useCallback((el) => {
    if (!el) return;
    el.querySelectorAll('pre').forEach(pre => {
      if (pre.querySelector('.mc-copy-btn')) return;
      const btn = document.createElement('button');
      btn.className = 'mc-copy-btn';
      btn.innerHTML = ICONS.copy;
      btn.title = 'Copy';
      btn.setAttribute('aria-label', 'Copy code');
      pre._copyBtn = btn;
      btn.addEventListener('click', () => {
        const code = pre.querySelector('code')?.textContent || pre.textContent;
        navigator.clipboard?.writeText(code).then(() => {
          btn.innerHTML = ICONS.check;
          setTimeout(() => { btn.innerHTML = ICONS.copy; }, 2000);
        });
      });
      pre.appendChild(btn);
    });
  }, []);

  return (
    <div
      ref={ref}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export function MessageItem({ msg, prevMsg, cfg }) {
  const isBot     = msg.role === 'bot';
  const isSystem  = msg.role === 'system' || msg.role === 'system-error';
  const isGrouped = prevMsg?.role === msg.role;

  if (msg.role === 'system-error') {
    return <ErrorCard msg={msg} />;
  }

  if (isSystem) {
    return <div class="mc-system">{msg.text}</div>;
  }

  return (
    <div
      class={`mc-row${isBot ? '' : ' mc-row-user'}${isGrouped ? '' : ' mc-row-first'}${msg.animate ? '' : ' mc-no-anim'}`}
      data-id={msg.id}
      style={{ animation: msg.animate ? undefined : 'none' }}
    >
      {isBot && cfg.showAvatar && (
        <div class={`mc-av-sm${isGrouped && !cfg.showAvatarGrouped ? ' hidden' : ''}`}>
          {cfg.botAvatar
            ? <img src={cfg.botAvatar} alt={cfg.botName} loading="lazy" />
            : cfg.botInitials
          }
        </div>
      )}
      <div class="mc-bubble-wrap">
        {/* Show thinking/spinner only while tools are actively running (streaming + pending) */}
        {isBot && msg.streaming && msg.toolCalls?.length > 0 && (
          <ToolCallIndicator toolCalls={msg.toolCalls} />
        )}
        <div class={`mc-bubble${isBot ? '' : ' mc-bubble-user'}`}>
          <BubbleContent text={msg.text} />
        </div>
        {isBot && !msg.streaming && msg.structured && msg.structured.type !== 'text' && (
          <StructuredResponse structured={msg.structured} />
        )}
        {cfg.showTimestamps && (
          <time class="mc-ts" dateTime={new Date(msg.timestamp).toISOString()}>
            {formatTime(msg.timestamp, cfg.locale)}
          </time>
        )}
      </div>
    </div>
  );
}
